import React from 'react';

import {useUnit} from 'effector-react';
import {Redirect} from 'react-router-dom';

import {userModel} from '@/entities/user/model';
import {AuditPage} from '@/pages/Audit';
import {EmployersControlPage} from '@/pages/EployersControl';
import {SubscriptionsPage} from '@/pages/Subscriptions';
import {ROUTES} from '@app/routing/routes';
import {RouteData} from '@app/routing/types';
import {CreateInvoiceNew} from '@nds-taxpayers/create-invoice-new';

import {CounterpartiesPage} from '@pages/Counterparties';
import {DashboardPage} from '@pages/Dashboard';
import {ForgotPage} from '@pages/ForgotPassword';
import {ForgotRequestPage} from '@pages/ForgotRequest';
import {InvoicesPage} from '@pages/Invoices';
import {LoginPage} from '@pages/Login';
import {OfficerSummaryPage} from '@pages/OfficerSummary';
import {PdfViewerPage} from '@pages/PdfViewer';
import {PreferencesPage} from '@pages/Preferences';
import {InvoicePrintPage} from '@pages/Print';
import {Profile} from '@pages/Profile';
import {ChangePasswordPage} from '@pages/Profile/ChangePassword';
import {RelationsPage} from '@pages/Relations';
import {SetPasswordPage} from '@pages/SetPassword';
import {TaxpayerDetailsPage} from '@pages/TaxpayerDetails';
import {TaxpayersPage} from '@pages/Taxpayers';
import {TwoFAPage} from '@pages/TwoFA';

export const PUBLIC_ROUTES_DATA: Record<string, RouteData> = {
    [ROUTES.LOGIN]: {
        component: <LoginPage />,
        exact: true,
    },
    [ROUTES.SET_PASSWORD]: {
        component: <SetPasswordPage />,
        exact: true,
    },
    [ROUTES.RESET_PASSWORD]: {
        component: <ForgotPage />,
        exact: true,
    },
    [ROUTES.REQUEST_RESET]: {
        component: <ForgotRequestPage />,
        exact: true,
    },
    [ROUTES.TWO_FA]: {
        component: <TwoFAPage />,
        exact: true,
    },
    [ROUTES.INVOICES_PDF_VIEW]: {
        component: <PdfViewerPage />,
        exact: true,
    },
    [ROUTES.INVOICES_PRINT]: {
        component: <InvoicePrintPage />,
        exact: true,
    },
};

interface InvoicesRouteProps {
    isSplitRoute?: boolean;
}

const InvoicesRoute = ({isSplitRoute = false}: InvoicesRouteProps) => {
    const isTaxpayer = useUnit(userModel.isTaxpayer);

    if (isTaxpayer && !isSplitRoute) return <Redirect to={ROUTES.INVOICES_INCOMING} />;
    if (!isTaxpayer && isSplitRoute) return <Redirect to={ROUTES.INVOICES} />;

    return <InvoicesPage />;
};

export const PRIVATE_ROUTES_DATA: Record<string, RouteData> = {
    [ROUTES.PROFILE]: {
        component: <Profile />,
        exact: true,
    },
    [ROUTES.CHANGE_PASSWORD]: {
        component: <ChangePasswordPage />,
        exact: true,
    },
    [ROUTES.DASHBOARD]: {
        component: <DashboardPage />,
        exact: true,
    },
    [ROUTES.OFFICER_SUMMARY]: {
        component: <OfficerSummaryPage />,
        exact: true,
    },
    [ROUTES.TAXPAYERS]: {
        component: <TaxpayersPage />,
        exact: true,
    },
    [ROUTES.TAXPAYER_DETAILS]: {
        component: <TaxpayerDetailsPage />,
        exact: false,
    },
    [ROUTES.TAXPAYER_DETAILS_NOTIFICATIONS]: {
        component: <TaxpayerDetailsPage />,
        exact: true,
    },
    [ROUTES.TAXPAYER_DETAILS_VIOLATIONS]: {
        component: <TaxpayerDetailsPage />,
        exact: true,
    },
    [ROUTES.TAXPAYER_DETAILS_DECLARATION]: {
        component: <TaxpayerDetailsPage />,
        exact: true,
    },
    [ROUTES.TAXPAYER_DETAILS_ACCOUNT]: {
        component: <TaxpayerDetailsPage />,
        exact: true,
    },
    [ROUTES.TAXPAYER_DETAILS_INVOICES]: {
        component: <TaxpayerDetailsPage />,
        exact: true,
    },
    [ROUTES.TAXPAYER_DETAILS_CONTRACTORS]: {
        component: <TaxpayerDetailsPage />,
        exact: true,
    },
    [ROUTES.RELATIONS]: {
        component: <RelationsPage />,
        exact: true,
    },
    [ROUTES.INVOICES]: {
        component: <InvoicesRoute />,
        exact: true,
    },
    [ROUTES.INVOICES_INCOMING]: {
        component: <InvoicesRoute isSplitRoute />,
        exact: true,
    },
    [ROUTES.INVOICES_OUTGOING]: {
        component: <InvoicesRoute isSplitRoute />,
        exact: true,
    },
    [ROUTES.DOCUMENTS_CREATE_INVOICE_ID]: {
        component: <CreateInvoiceNew />,
        exact: true,
    },
    [ROUTES.INVOICE_DETAILS]: {
        component: <CreateInvoiceNew />,
        exact: true,
    },
    [ROUTES.COUNTERPARTIES]: {
        component: <Redirect to={ROUTES.COUNTERPARTY_SELLERS} />,
        exact: true,
    },
    [ROUTES.COUNTERPARTY_SELLERS]: {
        component: <CounterpartiesPage />,
        exact: true,
    },
    [ROUTES.COUNTERPARTY_BUYERS]: {
        component: <CounterpartiesPage />,
        exact: true,
    },
    [ROUTES.COUNTERPARTY_INCOMING]: {
        component: <CounterpartiesPage />,
        exact: true,
    },
    [ROUTES.COUNTERPARTY_OUTGOING]: {
        component: <CounterpartiesPage />,
        exact: true,
    },
    [ROUTES.SUBSCRIPTIONS]: {
        component: <SubscriptionsPage />,
        exact: true,
    },
    [ROUTES.EMPLOYERS_CONTROL]: {
        component: <EmployersControlPage />,
        exact: true,
    },
    [ROUTES.AUDIT]: {
        component: <AuditPage />,
        exact: false,
    },
    [ROUTES.PREFERENCES]: {
        component: <PreferencesPage />,
        exact: false,
    },
};
